/**
 * #671 Discard task: the owner's control for the existing guarded `discard_task` RPC.
 *
 * There is no second discard path here. The database stays the authority: `discard_task`
 * requires owner AAL2, refuses a task a computer is running (`run_active`) or one another
 * unfinished task waits on (`conflict`), and keeps every run, summary and audit row.
 * `brunel.task_actions` says whether a task can be put aside and why not. This module only
 * picks that exact row for a Work Board card, sends one request behind the owner's AAL2
 * guard, and re-reads canonical truth afterwards instead of changing anything locally.
 */
import type { TaskActionRow } from './data';
import type { WorkBoardCard, WorkBoardSourceKind } from './work-board';

/** Cards that are not a canonical task record never get the action, even with a task id. */
const NOT_A_TASK = new Set<WorkBoardSourceKind>(['roadmap', 'plan', 'command_plan', 'approval']);

/**
 * The one canonical action row for a card: same organization, same exact task id. Anything
 * else (no task id, a non-task card, no row or more than one) offers nothing.
 */
export function taskActionFor(
  card: WorkBoardCard,
  orgId: string,
  rows: readonly TaskActionRow[],
): TaskActionRow | null {
  if (card.taskId === null || NOT_A_TASK.has(card.sourceKind)) return null;
  if (card.projectId == null) return null;
  const matches = rows.filter((row) => row.org_id === orgId
    && row.project_id === card.projectId
    && row.task_id === card.taskId);
  return matches.length === 1 ? matches[0]! : null;
}

/** The owner-facing sentence for a refusal from `discard_task`. */
export function discardTaskMessage(code: string): string {
  switch (code) {
    case 'run_active': return 'Brunel kept this task: a computer is running it. Stop that run first, then discard it.';
    case 'conflict': return 'Brunel kept this task: another unfinished task is waiting on it. Deal with that task first.';
    case 'forbidden': return 'Only the owner can discard a task, after confirming it is you (two-step verification).';
    case 'unauthorized': return 'Your session has expired. Sign in again to discard a task.';
    case 'not_found': return 'Brunel has no such task any more. Refresh and try again.';
    case 'invalid_input': return 'This task could not be identified. Refresh and try again.';
    default: return 'The task was not discarded.';
  }
}

export interface DiscardTaskResult {
  readonly ok: boolean;
  readonly message: string;
}

type RpcAnswer = { ok?: boolean; error?: { code?: string } } | null;

/**
 * Send exactly `{orgId, projectId, taskId}` once, behind the owner's AAL2 guard. After a
 * success or an uncertain answer (a lost response or a thrown transport error) the exact
 * task is re-read: `discarded` there is success, whatever the response said. The request
 * is never replayed. Every outcome ends with the room re-reading its canonical rows.
 */
export async function discardTask(input: {
  readonly action: TaskActionRow;
  readonly guard: (name: 'post_owner_command', action: () => Promise<void>) => Promise<void>;
  readonly rpc: (name: 'discard_task', args: { readonly request: unknown }) => PromiseLike<{ data: unknown; error: unknown }>;
  /** Re-reads the one exact task_actions row; null when the read itself failed. */
  readonly reread: () => Promise<TaskActionRow | null>;
  /** Re-reads the room's canonical rows so the card moves only from Brunel state. */
  readonly refresh: () => Promise<void>;
  readonly onResult: (result: DiscardTaskResult) => void;
}): Promise<void> {
  // discard_task requires owner aal2; the guard asks for step-up first when needed.
  await input.guard('post_owner_command', async () => {
    const { action } = input;
    let answer: RpcAnswer = null;
    let failed = false;
    try {
      const { data, error } = await input.rpc('discard_task', {
        request: { orgId: action.org_id, projectId: action.project_id, taskId: action.task_id },
      });
      answer = data as RpcAnswer;
      failed = Boolean(error);
    } catch {
      failed = true;
    }

    const refused = !failed && answer?.ok === false;
    if (refused) {
      input.onResult({ ok: false, message: discardTaskMessage(answer?.error?.code ?? '') });
    } else {
      // Success, or no trustworthy answer: the exact canonical row decides what happened.
      const confirmed = answer?.ok === true && !failed;
      const now = await input.reread().catch(() => null);
      if (now?.status === 'discarded') {
        input.onResult({ ok: true, message: 'Task discarded. Its runs and history are kept.' });
      } else if (confirmed) {
        input.onResult({ ok: true, message: 'Brunel accepted the discard. Its runs and history are kept.' });
      } else if (now === null) {
        input.onResult({ ok: false, message: 'Brunel did not confirm the discard, and the task could not be re-read. Refresh before trying again.' });
      } else {
        input.onResult({ ok: false, message: `Brunel did not confirm the discard. The task is still ${now.status}.` });
      }
    }
    await input.refresh();
  });
}
